import { createSlice } from '@reduxjs/toolkit';

import { errorHandler, isEmptyObj } from 'src/common/helpers';
import {
    UserBaseInfo,
    FavoritePayload,
    TotalAssets,
    UserVerifyInfo,
    UserInviteInfo,
    UserAccountLevelInfo,
} from 'src/types';

interface UserState {
    userInfoLoading: boolean;
    openFuturesLoading: boolean;
    userinfo: UserBaseInfo;
    userVerifyInfo: UserVerifyInfo;
    userInviteInfo: UserInviteInfo;
    userAccountLevelInfo: UserAccountLevelInfo;
    assets: TotalAssets;
    favorites: string[];
    isLogin: boolean;
    isTokenReady: boolean;
    loading: boolean;
    error?: string;
}
interface PairsObject {
    [key: string]: boolean;
}

const initialState: UserState = {
    userInfoLoading: false,
    openFuturesLoading: false,
    userinfo: JSON.parse(window.sessionStorage.getItem('userinfo') || '{}') as UserBaseInfo,
    userVerifyInfo: {} as UserVerifyInfo,
    userInviteInfo: {} as UserInviteInfo,
    userAccountLevelInfo: {} as UserAccountLevelInfo,
    assets: {} as TotalAssets,
    isLogin: !isEmptyObj(JSON.parse(window.sessionStorage.getItem('userinfo') || '{}')),
    isTokenReady: false,
    favorites: Object.keys(JSON.parse(window.localStorage.getItem('favorite') || '{}')) as string[],
    loading: false,
    error: undefined,
};

export const callTypes = {
    loading: 'loading',
    userInfoLoading: 'userInfoLoading',
    openFuturesLoading: 'openFuturesLoading',
};

export const userSlice = createSlice({
    name: 'userData',
    initialState,
    reducers: {
        startCall: (state, action) => {
            const key = action.payload as keyof UserState;
            return {
                ...state,
                error: undefined,
                ...(key in state && { [key]: true }),
            };
        },
        catchError: (state, action) => {
            const error = errorHandler(action.payload.error);
            const key = action.payload.key as keyof UserState;
            return {
                ...state,
                ...(error && { error: error.title }),
                ...(key in state && { [key]: false }),
            };
        },
        saveUserBaseInfo: (state, action) => {
            state.userInfoLoading = false;
            const userInfo: UserBaseInfo = action.payload;
            state.userinfo = userInfo;
            state.favorites = userInfo.favorites.map((fav) => fav.symbolId);
            state.isLogin = true;
            window.sessionStorage.userinfo = JSON.stringify(userInfo);
        },
        saveUserLevelInfo: (state, action) => {
            state.loading = false;
            state.userAccountLevelInfo = action.payload;
        },
        favoriteCreated: (state, action) => {
            state.loading = false;
            const payload: FavoritePayload = action.payload;
            state.favorites.push(payload.symbol_ids);
            if (!state.isLogin) {
                const pairs: PairsObject = {};
                state.favorites.forEach((pair) => {
                    pairs[pair] = true;
                });
                window.localStorage.favorite = JSON.stringify(pairs);
            }
        },
        favoriteCanceled: (state, action) => {
            state.loading = false;
            const payload: FavoritePayload = action.payload;
            state.favorites = state.favorites.filter((fav) => fav !== payload.symbol_ids) || [];
            if (!state.isLogin) {
                const pairs: PairsObject = {};
                state.favorites.forEach((pair) => {
                    pairs[pair] = true;
                });
                window.localStorage.favorite = JSON.stringify(pairs);
            }
        },
        logout: (state) => {
            state.loading = false;
            state.userinfo = {} as UserBaseInfo;
            state.favorites = Object.keys(
                JSON.parse(window.localStorage.getItem('favorite') || '{}'),
            ) as string[];
            state.isLogin = false;
        },
        userAssetsFetched: (state, action) => {
            state.assets = action.payload;
        },
        saveUserVerifyInfo: (state, action) => {
            state.userVerifyInfo = action.payload;
        },
        saveUserInviteCode: (state, action) => {
            state.userInviteInfo = action.payload;
        },
        tokenRefreshed: (state, action) => {
            state.isTokenReady = action.payload;
        },
        saveOpenFutures: (state, action) => {
            state.openFuturesLoading = false;
            const userInfo = { ...state.userinfo, openFuture: action.payload.success };
            state.userinfo = userInfo;
            window.sessionStorage.userinfo = JSON.stringify(userInfo);
        },
    },
});
